import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Login as LoginIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Tenant {
  id: number;
  name: string;
  slug: string;
  api_key: string;
  is_active: boolean;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  timezone: string;
  notes: string | null;
}

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

export default function AdminTenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [form, setForm] = useState({ name: '', slug: '', contact_name: '', contact_email: '', contact_phone: '', timezone: 'America/Chicago', notes: '' });

  const load = () => api.get('/api/tenants').then(r => setTenants(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleOpen = (tenant?: Tenant) => {
    if (tenant) {
      setEditing(tenant);
      setForm({
        name: tenant.name,
        slug: tenant.slug,
        contact_name: tenant.contact_name || '',
        contact_email: tenant.contact_email || '',
        contact_phone: tenant.contact_phone || '',
        timezone: tenant.timezone || 'America/Chicago',
        notes: tenant.notes || '',
      });
    } else {
      setEditing(null);
      setForm({ name: '', slug: '', contact_name: '', contact_email: '', contact_phone: '', timezone: 'America/Chicago', notes: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.put(`/api/tenants/${editing.id}`, form);
    } else {
      await api.post('/api/tenants', form);
    }
    setDialogOpen(false);
    load();
  };

  const handleAutoLogin = async (tenant: Tenant) => {
    try {
      const res = await api.post('/api/auth/auto-login', { tenant_id: tenant.id });
      const { token, tenant_slug } = res.data;
      // Open tenant portal in new tab with auto-login token
      const url = `https://${tenant_slug}.sa1-dal.watchcenter365.com/auto-login?token=${token}&tenant_id=${tenant.id}&tenant_name=${encodeURIComponent(tenant.name)}&timezone=${encodeURIComponent(tenant.timezone || 'America/Chicago')}`;
      window.open(url, '_blank');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Auto-login failed');
    }
  };

  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tenants</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Tenant
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Timezone</TableCell>
              <TableCell>API Key</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map(t => (
              <TableRow key={t.id}>
                <TableCell>{t.name}</TableCell>
                <TableCell><code>{t.slug}</code></TableCell>
                <TableCell>
                  {t.contact_name && <div>{t.contact_name}</div>}
                  {t.contact_email && <Typography variant="caption" color="text.secondary">{t.contact_email}</Typography>}
                </TableCell>
                <TableCell>{t.timezone}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <code style={{ fontSize: '0.75rem' }}>{t.api_key.slice(0, 12)}...</code>
                    <Tooltip title="Copy API Key">
                      <IconButton size="small" onClick={() => copyApiKey(t.api_key)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip size="small" label={t.is_active ? 'Active' : 'Inactive'} color={t.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(t)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Auto-Login to Tenant Portal">
                    <IconButton size="small" color="primary" onClick={() => handleAutoLogin(t)}>
                      <LoginIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Tenant' : 'Add Tenant'}</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="normal" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <TextField label="Slug" fullWidth margin="normal" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} disabled={!!editing} helperText="Used in subdomain URL (e.g., slug.sa1-dal.watchcenter365.com)" />
          <TextField label="Contact Name" fullWidth margin="normal" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} />
          <TextField label="Contact Email" fullWidth margin="normal" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
          <TextField label="Contact Phone" fullWidth margin="normal" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
          <TextField label="Timezone" fullWidth margin="normal" select value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })}>
            {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
          </TextField>
          <TextField label="Notes" fullWidth margin="normal" multiline rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
